import { useDroppable } from '@dnd-kit/core';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { debounce } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  HeaderPanel,
  OverflowAwareText,
} from '~components/Order/ordersDispatchStyledComponents';
import TabNumberAdornment from '~components/Tabs/TabNumberAdornment';
import Tabs, { TabProps } from '~components/Tabs/Tabs';
import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import {
  DispatchURLParams,
  OrdersDispatchStorageKeys,
  UnassignedViewMode,
} from '~constants/dispatch';
import { useStores } from '~store/RootStore';

import { JOB_CARD_OFFSET, JOB_COLUMN_WIDTH } from './constants';
import { SearchInput } from './Filters/SearchInput';
import UnassignedJobsFilters from './Filters/UnassignedJobsFilters';
import { UnassignedJobsList } from './UnassignedJobsList';
import { UnassignedOrders } from './UnassignedOrders';

const VALID_VIEW_MODES = Object.values(UnassignedViewMode);
const DEFAULT_VIEW_MODE = UnassignedViewMode.ORDERS;

const isValidViewMode = (value: string | null): value is UnassignedViewMode =>
  VALID_VIEW_MODES.includes(value as UnassignedViewMode);

const DroppableJobsColumn = ({ children }: { children: ReactNode }) => {
  const { setNodeRef } = useDroppable({ id: 'jobs-column' });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        width: `${JOB_COLUMN_WIDTH}px`,
        height: '100%',
        minHeight: `calc(100vh - ${JOB_CARD_OFFSET}px + 97px - var(--tread-agent-dock-height, 0px))`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {children}
    </Box>
  );
};

const resolveInitialViewMode = (paramValue: string | null): UnassignedViewMode => {
  if (isValidViewMode(paramValue)) return paramValue;
  const stored = localStorage.getItem(OrdersDispatchStorageKeys.UNASSIGNED_VIEW_MODE);
  if (isValidViewMode(stored)) return stored;
  return DEFAULT_VIEW_MODE;
};

export const UnassignedColumn = observer(() => {
  const theme = useTheme();
  const { driverSchedulerStore } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const [unassignedJobsInput, setUnassignedJobsInput] = useState('');

  const paramValue = searchParams.get(DispatchURLParams.UNASSIGNED_VIEW_MODE);
  const viewMode = resolveInitialViewMode(paramValue);

  useEffect(() => {
    if (paramValue !== viewMode) {
      setSearchParams(
        (params) => {
          params.set(DispatchURLParams.UNASSIGNED_VIEW_MODE, viewMode);
          return params;
        },
        { replace: true },
      );
    }
    localStorage.setItem(OrdersDispatchStorageKeys.UNASSIGNED_VIEW_MODE, viewMode);
  }, [paramValue, viewMode, setSearchParams]);

  const handleViewModeChange = useCallback(
    (next: UnassignedViewMode) => {
      setSearchParams((params) => {
        params.set(DispatchURLParams.UNASSIGNED_VIEW_MODE, next);
        return params;
      });
    },
    [setSearchParams],
  );

  const applyUnassignedJobFilter = useMemo(
    () =>
      debounce((value: string) => {
        driverSchedulerStore.setUnassignedJobsFilters({ search: value }, true);
      }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS),
    [driverSchedulerStore],
  );

  useEffect(() => {
    applyUnassignedJobFilter(unassignedJobsInput);

    return () => {
      applyUnassignedJobFilter.cancel();
    };
  }, [unassignedJobsInput, applyUnassignedJobFilter]);

  const orderCount = driverSchedulerStore.unassignedOrderGroups.length;
  const jobCount = driverSchedulerStore.unassignedJobs.length;

  // Grouping is only truthful once every unassigned job is in memory, on either
  // sub-tab: while the eager load is still paging, orderCount counts the groups
  // formed from the pages loaded so far. Suppress the adornment rather than show
  // a partial figure as a total.
  const orderCountIsComplete = driverSchedulerStore.isUnassignedJobsFullyLoaded;

  const viewModeTabs: TabProps<UnassignedViewMode>[] = [
    {
      label: t('dispatch.drivers.view_mode_orders'),
      value: UnassignedViewMode.ORDERS,
      endAdornment: orderCountIsComplete ? (
        <TabNumberAdornment count={orderCount} />
      ) : undefined,
      dataUi: 'unassigned-view-mode-orders',
    },
    {
      label: t('dispatch.drivers.view_mode_jobs'),
      value: UnassignedViewMode.JOBS,
      endAdornment: <TabNumberAdornment count={jobCount} />,
      dataUi: 'unassigned-view-mode-jobs',
    },
  ];

  return (
    <Box
      aria-label="unassigned-jobs-column"
      sx={{
        flexShrink: 0,
        minHeight: '100%',
        width: `${JOB_COLUMN_WIDTH}px`,
        display: 'flex',
        flexDirection: 'column',
        background: theme.semantic.surfaceRaised,
        borderRight: `1px solid ${theme.brandV2.colors.treadGray7}`,
        p: `0 ${theme.spacing(1)}`,
      }}
    >
      <HeaderPanel sx={{ mb: 2, borderBottom: 'none', top: 0 }}>
        <OverflowAwareText sx={{ mt: 1, fontWeight: 600 }}>
          {t('dispatch.drivers.unassigned_jobs')}:{' '}
          {dayjs.tz(driverSchedulerStore.dateFilters.startDate).format("ddd MMM DD, 'YY")}
        </OverflowAwareText>

        <Box
          sx={{
            mt: 1.25,
            display: 'flex',
            gap: 1,
          }}
        >
          <UnassignedJobsFilters />

          <SearchInput onChange={setUnassignedJobsInput} value={unassignedJobsInput} />
        </Box>

        <Box sx={{ mt: 1 }}>
          <Tabs<UnassignedViewMode>
            onChange={handleViewModeChange}
            selected={viewMode}
            tabs={viewModeTabs}
          />
        </Box>
      </HeaderPanel>
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
        }}
      >
        <DroppableJobsColumn>
          {viewMode === UnassignedViewMode.ORDERS ? (
            <UnassignedOrders />
          ) : (
            <UnassignedJobsList />
          )}
        </DroppableJobsColumn>
      </Box>
    </Box>
  );
});
